import { currenciesValues, numberWithCommas } from '@shared'
import { getTypeLabel } from '@components/organisms/Limits/LimitsExtended.constants'

/**
 * Creates a notification object with shared boilerplate.
 */
function createNotification(message, type = 'success', title = 'Message') {
  return {
    title,
    message,
    type,
    insert: 'top',
    container: 'top-right',
    animationIn: ['animated', 'fadeIn'],
    animationOut: ['animated', 'fadeOut'],
    dismiss: {
      duration: 5000,
      onScreen: true,
    },
  }
}

/**
 * Creates a notification function that builds the message dynamically.
 */
function createNotificationFn(messageFn, type = 'success', title = 'Message') {
  return (...args) => createNotification(messageFn(...args), type, title)
}

// Generic error message
export const GENERIC_ERROR_NOTIFICATION = createNotification(
  'An error has ocurred',
  'danger',
  'Alert',
)

// Bonuses messages
export const BONUS_CREATION_SUCCESS_MESSAGE = createNotification(
  'Successful creation of bonus.',
  'success',
  'Message',
)

export const BONUS_CREATION_FAILURE_MESSAGE = createNotification(
  'Failed creation of bonus, please try again or contact support.',
  'danger',
  'Message',
)

export const BONUS_EDIT_SUCCESS_MESSAGE = createNotification(
  'Bonus edited successfully',
  'success',
  'Message',
)

export const BONUS_EDIT_FAILURE_MESSAGE = createNotification(
  'Bonus edit failed, please try again or contact support.',
  'danger',
  'Message',
)

// Manual adjusment messages
export const TRANSACTION_SUCCESS_AMOUNT = createNotificationFn(
  (currency, amount) =>
    `Successful  Manual Adjustment in Cash balance of ${numberWithCommas(currenciesValues, amount, currency)}`,
  'success',
  'Message',
)

export const TRANSACTION_SUCCESS_BONUS = createNotificationFn(
  (currency, bonus) =>
    `Successful  Manual Adjustment in Bonus of  ${numberWithCommas(currenciesValues, bonus, currency)}`,
  'success',
  'Message',
)

export const TRANSACTION_FAILURE = createNotificationFn(
  (error) => error,
  'danger',
  'Message',
)

export const CUSTOM_SUCCESS_NOTIFICATION = createNotificationFn(
  (data) => data,
  'success',
  'Message',
)

export const CUSTOM_ERROR_NOTIFICATION = createNotificationFn(
  (data) => data,
  'danger',
  'Message',
)

// ClearStake KYC recovery messages
export const CLEARSTAKE_INITIATE_SUCCESS = createNotification(
  'ClearStake verification initiated. Share the journey link with the player.',
  'success',
  'ClearStake',
)

export const CLEARSTAKE_CANCEL_SUCCESS = createNotification(
  'ClearStake case cancelled.',
  'success',
  'ClearStake',
)

export const CLEARSTAKE_LINK_COPIED = createNotification(
  'Journey link copied to clipboard.',
  'success',
  'ClearStake',
)

export const CLEARSTAKE_LINK_COPY_FAILURE = createNotification(
  'Could not copy the journey link. Please copy it manually.',
  'danger',
  'ClearStake',
)

// Manual adjustment invalid status message
export const INVALID_STATUS = createNotification(
  "The player's current status is not allowed to make this operation.",
  'danger',
  'Message',
)

// Manual adjustment user not allowed message
export const INVALID_ROLE = createNotification(
  'The operator is not allowed to make this operation.',
  'danger',
  'Message',
)

// Campaigns messages
export const CAMPAIGN_CREATION_SUCCESS_MESSAGE = createNotification(
  'Successful creation of campaign.',
  'success',
  'Message',
)

export const CAMPAIGN_CREATION_FAILURE_MESSAGE = createNotification(
  'Failed creation of campaign, please try again or contact support.',
  'danger',
  'Message',
)

export const CAMPAIGN_ENABLE_SUCCESS_MESSAGE = createNotification(
  'Successful update of campaign.',
  'success',
  'Message',
)

export const CAMPAIGN_ENABLE_FAILURE_MESSAGE = createNotification(
  'Failed update of campaign, please try again or contact support.',
  'danger',
  'Message',
)

// Limits messages
// Delete limits
export const DELETE_LIMIT_MESSAGE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(
      type,
    )} limits have been successfully deleted. This deletion will take effect in 24 hours.`,
  'success',
  'Success!',
)

export const DELETE_LIMIT_MESSAGE_FAILURE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(
      type,
    )} limits have been not successfully deleted, please try again or contact support.`,
  'danger',
  'Message!',
)

// Create limits
export const CREATE_LIMIT_MESSAGE = createNotificationFn(
  (type) => `Your ${getTypeLabel(type)} limits have been successfully created.`,
  'success',
  'Success!',
)

export const CREATE_LIMIT_MESSAGE_FAILURE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(
      type,
    )} limits have been not successfully created, please try again or contact support.`,
  'danger',
  'Message!',
)

// Update limits
export function UPDATE_LIMIT_MESSAGE(type, limitIncreased) {
  const label = getTypeLabel(type)
  const message = limitIncreased
    ? `Your ${label} limits have been successfully updated. The increase will be reflected in 24 hours.`
    : `Your ${label} limits have been successfully updated.`
  return createNotification(message, 'success', 'Success!')
}

export const UPDATE_LIMIT_MESSAGE_FAILURE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(
      type,
    )} limits have been not successfully updated, please try again or contact support.`,
  'danger',
  'Message!',
)

// Operator cap messages
export const CAP_UPDATE_SUCCESS_MESSAGE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(type)} operator cap has been successfully updated.`,
  'success',
  'Success!',
)

export const CAP_DELETE_SUCCESS_MESSAGE = createNotificationFn(
  (type) =>
    `Your ${getTypeLabel(type)} operator cap has been successfully deleted.`,
  'success',
  'Success!',
)

// Player details update messages
export const DETAILS_FAILURE_MESSAGE = createNotification(
  'Failed to change player details, please try again or contact support.',
  'danger',
  'Error!',
)

export const DETAILS_SUCCESS_MESSAGE = createNotification(
  'Player details change has been successfully saved.',
  'success',
  'Success!',
)

export const MARKETING_CHANGE_SUCCESS_MESSAGE = createNotification(
  'Player marketing preferences have been successfully saved.',
  'success',
  'Success!',
)

// Notes messages
export const NOTES_FAILURE_MESSAGE = createNotification(
  'Failed to save your changes, please try again or contact support.',
  'danger',
  'Error!',
)

export const NOTES_SUCCESS_MESSAGE = createNotification(
  'Your note has been successfully saved.',
  'success',
  'Success!',
)

export const NOTES_DELETE_FAILURE_MESSAGE = createNotification(
  'Failed to delete note, please try again or contact support.',
  'danger',
  'Error!',
)

export const NOTES_EDIT_SUCCESS_MESSAGE = createNotification(
  'Your note has been successfully edited.',
  'success',
  'Success!',
)

export const NOTES_EDIT_FAILURE_MESSAGE = createNotification(
  'Failed to edited note, please try again or contact support.',
  'danger',
  'Error!',
)

export const ATTACHMENTS_EDIT_FAILURE_MESSAGE = createNotification(
  'Failed to edit attachment, please try again or contact support.',
  'danger',
  'Error!',
)

export const ATTACHMENTS_EDIT_SUCCESS_MESSAGE = createNotification(
  'Your attachment has been successfully saved.',
  'success',
  'Success!',
)

export const NOTES_DELETE_SUCCESS_MESSAGE = createNotification(
  'Your note has been successfully deleted.',
  'success',
  'Success!',
)

// Player status update messages
export const STATUS_FAILURE_MESSAGE = createNotification(
  'Failed to change status, please try again or contact support.',
  'danger',
  'Error!',
)

export const STATUS_SUCCESS_MESSAGE = createNotification(
  'Your status change has been successfully saved.',
  'success',
  'Success!',
)

export const E_KYC_SUCCESS = createNotification(
  'KYC check has been done successfully.',
  'success',
  'Success!',
)

export const E_KYC_FAIL = createNotificationFn(
  (error) => error,
  'danger',
  'Message',
)

export const SAVE_LIFETIME_DEPOSIT_LIMIT_SUCCESS = createNotification(
  'New affordability limit successfully saved',
  'success',
  'Success!',
)

export const SAVE_LIFETIME_DEPOSIT_LIMIT_FAILURE = createNotification(
  'Failed to save affordability limit, please try again or contact support.',
  'danger',
  'Error!',
)

export const SAVE_AFFORDABILITY_DEPOSIT_LIMIT_SUCCESS = createNotification(
  'New affordability deposit limit successfully saved',
  'success',
  'Success!',
)

export const SAVE_AFFORDABILITY_DEPOSIT_LIMIT_FAILURE = createNotification(
  'Failed to save affordability deposit limit, please try again or contact support.',
  'danger',
  'Error!',
)

export const PAYMENT_CHARGE_CREATE_SUCCESS_MESSAGE = createNotification(
  'Payment charge configuration created successfully.',
  'success',
  'Success!',
)

export const PAYMENT_CHARGE_CREATE_FAILURE_MESSAGE = createNotification(
  'Failed to create payment charge configuration. Please try again or contact support.',
  'danger',
  'Error!',
)

export const PAYMENT_CHARGE_UPDATE_SUCCESS_MESSAGE = createNotification(
  'Payment charge configuration updated successfully.',
  'success',
  'Success!',
)

export const PAYMENT_CHARGE_UPDATE_FAILURE_MESSAGE = createNotification(
  'Failed to update payment charge configuration. Please try again or contact support.',
  'danger',
  'Error!',
)

// Payment method messages
export const PAYMENT_METHOD_CREATE_SUCCESS_MESSAGE = createNotification(
  'Payment method created successfully.',
  'success',
  'Success!',
)

export const PAYMENT_METHOD_CREATE_FAILURE_MESSAGE = createNotification(
  'Failed to create payment method. Please try again or contact support.',
  'danger',
  'Error!',
)

export const PAYMENT_METHOD_UPDATE_SUCCESS_MESSAGE = createNotification(
  'Payment method updated successfully.',
  'success',
  'Success!',
)

export const PAYMENT_METHOD_UPDATE_FAILURE_MESSAGE = createNotification(
  'Failed to update payment method. Please try again or contact support.',
  'danger',
  'Error!',
)

export const PAYMENT_METHOD_DELETE_SUCCESS_MESSAGE = createNotification(
  'Payment method deleted successfully.',
  'success',
  'Success!',
)

export const PAYMENT_METHOD_DELETE_FAILURE_MESSAGE = createNotification(
  'Failed to delete payment method. Please try again or contact support.',
  'danger',
  'Error!',
)

// Site configuration messages
export const SITE_CONFIG_SAVE_SUCCESS_MESSAGE = createNotification(
  'Site configuration saved successfully.',
  'success',
  'Success!',
)

export const SITE_CONFIG_SAVE_FAILURE_MESSAGE = createNotification(
  'Failed to save site configuration. Please try again or contact support.',
  'danger',
  'Error!',
)

export const SITE_CONFIG_DELETE_SUCCESS_MESSAGE = createNotification(
  'Site configuration deleted successfully.',
  'success',
  'Success!',
)

export const SITE_CONFIG_DELETE_FAILURE_MESSAGE = createNotification(
  'Failed to delete site configuration. Please try again or contact support.',
  'danger',
  'Error!',
)

// Bonus status messages
export const BONUS_PUBLISH_SUCCESS_MESSAGE = createNotification(
  'Bonus published successfully.',
  'success',
  'Message',
)

export const BONUS_PUBLISH_FAILURE_MESSAGE = createNotification(
  'Failed to publish bonus, please try again or contact support.',
  'danger',
  'Message',
)

export const BONUS_DELETE_SUCCESS_MESSAGE = createNotification(
  'Bonus deleted successfully.',
  'success',
  'Message',
)

export const BONUS_DELETE_FAILURE_MESSAGE = createNotification(
  'Failed to delete bonus, please try again or contact support.',
  'danger',
  'Message',
)

// Campaign status messages
export const CAMPAIGN_PUBLISH_SUCCESS_MESSAGE = createNotification(
  'Campaign published successfully.',
  'success',
  'Message',
)

export const CAMPAIGN_PUBLISH_FAILURE_MESSAGE = createNotification(
  'Failed to publish campaign, please try again or contact support.',
  'danger',
  'Message',
)

export const CAMPAIGN_DELETE_SUCCESS_MESSAGE = createNotification(
  'Campaign deleted successfully.',
  'success',
  'Message',
)

export const CAMPAIGN_DELETE_FAILURE_MESSAGE = createNotification(
  'Failed to delete campaign, please try again or contact support.',
  'danger',
  'Message',
)

export const CAMPAIGN_UPDATE_SUCCESS_MESSAGE = createNotification(
  'Campaign updated successfully.',
  'success',
  'Message',
)

export const CAMPAIGN_UPDATE_FAILURE_MESSAGE = createNotification(
  'Failed to update campaign, please try again or contact support.',
  'danger',
  'Message',
)

// Assignment cancel messages
export const ASSIGNMENT_CANCEL_SUCCESS_MESSAGE = createNotification(
  'Assignment cancelled successfully.',
  'success',
  'Message',
)

export const ASSIGNMENT_CANCEL_FAILURE_MESSAGE = createNotification(
  'Failed to cancel assignment, please try again or contact support.',
  'danger',
  'Message',
)
